export const site = {
  name: 'White Ibis',
  legalStatus: 'Founder-led Florida digital agency',
  url: 'https://whiteibis.ai',
  founder: 'Troy Chiappone',
  availability: 'Now accepting select engagements',
  positioning: 'Digital presence. Personal attention.',
  summary:
    'Work directly with Troy Chiappone on eight connected services: local search, websites, advertising, lead follow-up, customer retention, booking and payments, branding, and content.',
} as const;

export const navigation = [
  { label: 'Services', href: '/services' },
  { label: 'Work', href: '/work' },
  { label: 'Process', href: '/process' },
  { label: 'About', href: '/about' },
] as const;

export type Service = {
  number: string;
  slug: string;
  title: string;
  short: string;
  problem: string;
  deliverables: string[];
  ownership: string;
  handoff: string;
};

export const services: Service[] = [
  {
    number: '01', slug: 'local-presence', title: 'Local SEO & Maps',
    short: 'Accurate listings, useful service pages, and a steady review routine. Help nearby customers discover your business.',
    problem: 'Outdated listings, inconsistent business details, and thin service pages can make an established local business difficult to find.',
    deliverables: ['Google Business Profile and Apple Business Connect setup or improvements', 'Consistent hours, service areas, contact details, and business photos', 'Local search improvements for relevant services and locations', 'Review-request workflows, profile updates, and visibility reporting'],
    ownership: 'Your business remains the primary owner of its listings and search accounts. Platform verification and approvals stay with the provider.',
    handoff: 'Accurate profiles, documented access, and an agreed routine for listing updates, reviews, and local search improvements.',
  },
  {
    number: '02', slug: 'web-foundations', title: 'Websites & conversion',
    short: 'A distinctive, easy-to-use website with clear next steps for calling, requesting a quote, or booking your services.',
    problem: 'A dated or confusing website can leave visitors unsure what you offer, why to trust you, or how to take the next step.',
    deliverables: ['Responsive website design and implementation that reflect your brand', 'Clear service pages, landing pages, quote requests, and booking paths', 'Domain, hosting, search foundations, and inquiry tracking setup', 'Performance, accessibility, and mobile checks, with an agreed website care plan'],
    ownership: 'Your company controls the domain, hosting, website, analytics, and related accounts.',
    handoff: 'A tested website, documented accounts, and a practical plan for content updates and improvements informed by visitor behavior.',
  },
  {
    number: '03', slug: 'paid-ads', title: 'Paid ads & lead generation',
    short: 'Focused Google and Meta campaigns built around your services, service area, and capacity to take on new customers.',
    problem: 'Advertising without a clear offer, a useful landing page, or reliable inquiry tracking makes it difficult to see where your budget is going.',
    deliverables: ['Campaign planning around your ideal customers, service area, and agreed budget', 'Google Search or Meta campaign setup and management where appropriate', 'Campaign landing pages, inquiry tracking, and lead-quality feedback', 'Regular budget reviews, creative testing, and plain-language performance reporting'],
    ownership: 'Your business owns its advertising accounts, tracking data, and agreed final campaign assets. Advertising spend is separate from service fees.',
    handoff: 'Documented campaigns and measurement, with an agreed review cadence to connect ad spending to inquiries and booked work.',
  },
  {
    number: '04', slug: 'lead-automation', title: 'Lead follow-up & automation',
    short: 'Give every inquiry a clear place to go, a prompt response, and a manageable next step for you or your team.',
    problem: 'Inquiries spread across calls, inboxes, and forms are easy to lose track of, especially when the owner is busy doing the work.',
    deliverables: ['A simple customer relationship management system matched to your workflow', 'Connected website forms, inquiry routing, and staff notifications', 'Appropriate automated acknowledgments, follow-up reminders, and quote tracking', 'Workflow testing, staff guidance, and ongoing checks for broken connections'],
    ownership: 'Your company controls its customer records, communication accounts, and automation tools, with clear staff access and consent settings.',
    handoff: 'A tested inquiry-to-booking workflow, documented automations, and a clear routine for personal follow-up and system care.',
  },
  {
    number: '05', slug: 'customer-retention', title: 'Email & customer retention',
    short: 'Thoughtful newsletters, service reminders, and customer follow-up that give people a reason to come back.',
    problem: 'When communication ends after the first job, customers can forget to rebook, renew, or recommend a business they already trust.',
    deliverables: ['Email platform setup, sending-domain configuration, and signup consent', 'Branded newsletters and reusable campaign templates', 'Relevant service reminders, welcome messages, and customer reactivation campaigns', 'Customer-list organization, campaign management, and engagement reporting'],
    ownership: 'Your company controls its sending domain, subscriber list, email platform, and agreed final campaign assets.',
    handoff: 'A practical communication calendar, tested templates, and an agreed routine for campaigns, list care, and customer follow-up.',
  },
  {
    number: '06', slug: 'commerce-launch', title: 'Booking & payments',
    short: 'Make it easier to schedule a service, pay a deposit, or settle an invoice through tools that fit your business.',
    problem: 'Awkward scheduling, manual invoicing, and limited payment options create unnecessary back-and-forth for customers and your team.',
    deliverables: ['Connected appointment booking, quote acceptance, and scheduling reminders', 'Online invoices, payment links, deposits, and supported digital wallets', 'Shopify or WooCommerce setup and migration where an online store is appropriate', 'Payment-flow testing, staff guidance, and an agreed support routine'],
    ownership: 'Your business controls the booking system, store, customer data, and payment-provider relationship. Provider fees and approvals remain separate.',
    handoff: 'A tested booking and payment journey, with documented routines for appointments, invoices, orders, and refunds.',
  },
  {
    number: '07', slug: 'brand-identity', title: 'Brand identity & design',
    short: 'A recognizable logo, visual identity, and voice that feel like your business, wherever customers meet you.',
    problem: 'Your business has grown, but the logo, colors, photos, and messaging across your platforms no longer tell the same story.',
    deliverables: ['Logo design or a considered refresh of your existing identity', 'Color palette, typography, and practical brand guidance', 'Consistent social profiles, cover images, and reusable templates', 'Clear messaging and a rollout plan for your website, emails, and everyday materials'],
    ownership: 'Your business owns the agreed final brand assets and receives the usable files and guidelines.',
    handoff: 'An organized brand kit and practical guidance for keeping new materials consistent as your business grows.',
  },
  {
    number: '08', slug: 'ad-creative', title: 'Ad creative & content',
    short: 'Branded visuals, useful social content, and short videos that show what your business does and why it matters.',
    problem: 'Keeping up with content becomes difficult when every post starts from scratch and the visuals do not feel connected to your brand.',
    deliverables: ['A focused content plan around your services, customers, and chosen channels', 'Branded Facebook and Instagram ad creative and campaign variations', 'Short-form video editing for Reels or TikTok where relevant, using agreed source footage', 'Reusable social templates, publishing support, and content performance reviews'],
    ownership: 'Your business controls its social accounts and receives the agreed final creative assets, with any third-party asset licenses documented.',
    handoff: 'An organized content library, reusable templates, and a defined production and review schedule that fits your team.',
  },
];

export const processSteps = [
  {
    number: '01',
    title: 'Listen',
    summary: 'Establish the real starting point.',
    detail:
      'We review business goals, customer paths, current accounts, ownership, content, integrations, and operational friction. Unknowns are surfaced before they become build problems.',
    output: 'Audit findings, priorities, constraints, and an initial account map.',
  },
  {
    number: '02',
    title: 'Plan',
    summary: 'Choose a system your business can support.',
    detail:
      'We define the platform, information architecture, data movement, access roles, migration sequence, and launch responsibilities around your actual operation.',
    output: 'Scoped proposal, solution plan, ownership model, and delivery schedule.',
  },
  {
    number: '03',
    title: 'Build',
    summary: 'Connect design to day-to-day operations.',
    detail:
      'Design, content, product data, configurations, and integrations are implemented in company-owned accounts. Progress is reviewed in working increments.',
    output: 'A working system, review notes, content status, and documented decisions.',
  },
  {
    number: '04',
    title: 'Launch',
    summary: 'Test the whole path, not just the screen.',
    detail:
      'We test devices, forms, search, orders, taxes, payment flows, notifications, analytics, redirects, access, and staff workflows before coordinated release.',
    output: 'Launch checklist, tested production system, administrator access, and team training.',
  },
  {
    number: '05',
    title: 'Care',
    summary: 'Keep the foundation useful and current.',
    detail:
      'Post-launch support resolves early issues, maintains quality, and turns real operating feedback into an intentional improvement plan.',
    output: 'Support plan, maintenance cadence, updated documentation, and next priorities.',
  },
] as const;

export const concepts = [
  {
    status: 'Demonstration',
    title: 'The ownership map',
    type: 'Digital account architecture',
    description:
      'A practical model for separating company-owned platforms, staff access, specialist access, billing, recovery, and documentation.',
    takeaway:
      'The agency can help without becoming the permanent owner of the client’s domain, data, or credentials.',
  },
  {
    status: 'Independent concept',
    title: 'From store shelf to multi-location commerce',
    type: 'Retail workflow planning',
    description:
      'A capability exploration showing how catalog structure, inventory locations, pickup rules, shipping, taxes, payments, and staff routines fit together before a store goes live.',
    takeaway:
      'The storefront is only one layer; a stable launch depends on the operating model beneath it.',
  },
] as const;

export const auditAreas = [
  'Local search, Maps listings, and review routines',
  'Website clarity, mobile usability, and inquiry tracking',
  'Advertising readiness, budgets, and measurement',
  'Lead response, customer records, and follow-up workflows',
  'Email readiness and opportunities for repeat business',
  'Booking, invoicing, and payment experience',
  'Brand consistency across customer touchpoints',
  'Campaign creative, content, and publishing routines',
] as const;
